import { combineReducers } from 'redux';
import isEmpty from 'lodash/isEmpty';

import {
  ssrEligibilitySuccessActionData,
  ssrEligibilitySuccessMappedData,
} from './returns/ssrEligibility';
import ssrHrtvSuccess from './returns/ssrHrtv';
import {
  createCalendar,
  updateApplianceCalendarMonth,
  updateApplianceDeliveryDate,
} from './applianceCalendar';
import {
  eReceiptsDetailsToggle,
  fetchEReceiptsSummarySuccess,
  fetchEReceiptsDetails,
  fetchEReceiptsDetailSuccess,
  fetchEReceiptsDetailsFailure,
} from './eReceipts';
import {
  fetchOrderHistoryDetail,
  orderHistoryDetailToggle,
  fetchOrderHistoryOrderDetailsSuccess
} from './orderHistory';
import { convertOrderToCanceled, convertItemsToCanceled } from './onlineCancel';
import { ACTIONS } from '../constants';
import { returnServiceTypes } from '../constants/returns';

const {
  SSR_RESET,
  ELIGIBILITY_SUCCESS,
  HRTV_SUCCESS,
  CHECKBOX_SELECT,
  BOXES_SELECT,
  SET_SELECTED_BOXES_ERROR,
  SET_LANDING_ERROR,
  BACK_TO_LANDING,
  FETCH_REQUEST,
  RETURNS_500_ERROR,
  RETURNS_400_ERROR,
  SSR_CREATE_SUCCESS,
  SSR_CREATE_ERROR,
} = returnServiceTypes;

const orderActions = (filter: string) => {
  const actionData = (state: Object = {}, action: Object) => {
    if (filter !== action.filter) {
      return state;
    }
    switch (action.type) {
      case ACTIONS.FETCH_BODFS_DELIVERY_DETAIL_SUCCESS:
      case ACTIONS.FETCH_ORDER_DETAILS_SUCCESS:
      case ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_SUCCESS:
      case ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY_SUCCESS:
      case ACTIONS.MA_CANCEL_REQUEST_SUCCESS:
        return { ...state, ...action.response };
      case ACTIONS.ERECEIPTS_PAGE_SELECTION:
        return {
          ...state,
          currentPage: action.payload.page,
        };
      case ACTIONS.ERECEIPTS_DROPDOWN_SELECTION:
        return {
          ...state,
          [action.payload.selection]: action.payload.value,
          currentPage: 1,
        };
      case ACTIONS.ERECEIPTS_DETAILS_TOGGLE:
        return eReceiptsDetailsToggle(state, action);
      case ACTIONS.FETCH_ERECEIPTS_SUMMARY_SUCCESS:
        return fetchEReceiptsSummarySuccess(state, action);
      case ACTIONS.FETCH_ERECEIPTS_DETAILS:
        return fetchEReceiptsDetails(state, action);
      case ACTIONS.FETCH_ERECEIPTS_DETAILS_SUCCESS:
        return fetchEReceiptsDetailSuccess(state, action);
      case ACTIONS.FETCH_ERECEIPTS_DETAILS_FAILURE:
        return fetchEReceiptsDetailsFailure(state, action);
      case ACTIONS.FETCH_ORDER_HISTORY_SUCCESS:
        return {
          emailId: action.payload.emailId,
          orders: action.payload.orders,
          filteredOrders: action.payload.filteredOrders,
          currentFilter: action.payload.currentFilter,
          currentPage: 1,
        };
      case ACTIONS.FETCH_ORDER_HISTORY_ORDER_DETAILS_SUCCESS:
        return { ...state, ...action.payload.response };
      case ACTIONS.ORDER_HISTORY_DROPDOWN_SELECTION:
        return {
          ...state,
          currentFilter: action.currentFilter,
          currentPage: 1,
        };
      case ACTIONS.ORDER_HISTORY_PAGE_SELECTION:
        return { ...state, currentPage: action.currentPage };
      case ELIGIBILITY_SUCCESS:
        return ssrEligibilitySuccessActionData(state, action);
      case HRTV_SUCCESS:
        return ssrHrtvSuccess(state, action);
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_SUCCESS:
        return createCalendar(state, action);
      case ACTIONS.CHANGE_APPLIANCE_CALENDAR_MONTH:
        return updateApplianceCalendarMonth(state, action);
      case ACTIONS.SELECT_APPLIANCE_DELIVERY_DATE:
        return updateApplianceDeliveryDate(state, action);
      case ACTIONS.BOPIS_CANCEL_SUCCESS:
        return convertOrderToCanceled(state, action);
      case SSR_RESET:
      case ACTIONS.RESET_APPLIANCE_CALENDAR:
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS:
      case ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY:
        return {};
      default:
        return state;
    }
  };

  const mappedData = (state: Object = {}, action: Object) => {
    const data = {};
    if (filter !== action.filter) {
      return state;
    }
    switch (action.type) {
      case ACTIONS.FETCH_ORDER_DETAILS_SUCCESS:
      case ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY_SUCCESS:
        return { ...state, ...action.lineItems };
      case ACTIONS.FETCH_ORDER_HISTORY:
        return { ...state, hasLoaded: false };
      case ACTIONS.FETCH_ORDER_HISTORY_SUCCESS:
        return { ...state, ...action.payload.orderActions, hasLoaded: true };
      case ACTIONS.FETCH_ORDER_HISTORY_ORDER_META:
        return fetchOrderHistoryDetail(state, action);
      case ACTIONS.ORDER_HISTORY_DETAIL_TOGGLE:
        return orderHistoryDetailToggle(state, action);
      case ACTIONS.FETCH_ORDER_HISTORY_ORDER_DETAILS_SUCCESS:
        return { ...state, ...action.payload.lineItems };
      case ACTIONS.FETCH_ORDER_HISTORY_ORDER_META_SUCCESS:
        return fetchOrderHistoryOrderDetailsSuccess(state, action);
      case ACTIONS.FETCH_ORDER_HISTORY_ORDER_META_ERROR:
        return {
          ...state,
          [action.payload.orderId]: {
            ...state[action.payload.orderId],
            isFetching: false,
            isError: true,
          },
        };
      case ACTIONS.FETCH_ERECEIPTS_DETAILS_SUCCESS:
        return {
          ...state,
          [action.payload.receipt.receiptNumber]: {
            ...action.payload.receipt,
          },
        };

      case ACTIONS.FETCH_SHIPMENT_TRACKING_DETAIL:
      case ACTIONS.FETCH_BODFS_DELIVERY_DETAIL:
        data[action.id] = {};
        return data;
      case ACTIONS.FETCH_SHIPMENT_TRACKING_DETAIL_SUCCESS:
      case ACTIONS.FETCH_BODFS_DELIVERY_DETAIL_SUCCESS:
        data[action.id] = action.response;
        return data;
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_SUCCESS:
        return action.response;
      case SSR_RESET:
        return data;
      case ELIGIBILITY_SUCCESS:
        return ssrEligibilitySuccessMappedData(state, action);
      case ACTIONS.BOPIS_CANCEL_SUCCESS:
        return convertItemsToCanceled(state, action);
      default:
        return state;
    }
  };

  const isFetching = (state: boolean = false, action: Object) => {
    if (filter !== action.filter) {
      return state;
    }
    switch (action.type) {
      case ACTIONS.FETCH_ORDER_DETAILS:
      case ACTIONS.FETCH_ORDER_HISTORY:
      case ACTIONS.FETCH_ERECEIPTS_SUMMARY:
      case ACTIONS.FETCH_SHIPMENT_TRACKING_DETAIL:
      case ACTIONS.FETCH_RETURN_REQUEST:
      case ACTIONS.FETCH_BODFS_DELIVERY_DETAIL:
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS:
      case ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE:
      case ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY:
      case ACTIONS.MA_CANCEL_REQUEST:
      case FETCH_REQUEST:
        return true;
      case ACTIONS.FETCH_ORDER_DETAILS_SUCCESS:
      case ACTIONS.FETCH_ORDER_DETAILS_FAILURE:
      case ACTIONS.FETCH_ORDER_HISTORY_SUCCESS:
      case ACTIONS.FETCH_ORDER_HISTORY_FAILURE:
      case ACTIONS.FETCH_ERECEIPTS_SUMMARY_SUCCESS:
      case ACTIONS.FETCH_ERECEIPTS_SUMMARY_FAILURE:
      case ACTIONS.FETCH_SHIPMENT_TRACKING_DETAIL_SUCCESS:
      case ACTIONS.FETCH_SHIPMENT_TRACKING_DETAIL_FAILURE:
      case ACTIONS.FETCH_ORDER_DETAILS_EMAIL_FAILURE:
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_SUCCESS:
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_FAILURE:
      case ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_SUCCESS:
      case ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_FAILURE:
      case ACTIONS.FETCH_BODFS_DELIVERY_DETAIL_SUCCESS:
      case ACTIONS.SHOW_EARLY_BODFS_TYT_MODAL:
      case ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY_SUCCESS:
      case ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY_FAILURE:
      case ACTIONS.MA_CANCEL_REQUEST_SUCCESS:
      case ACTIONS.MA_CANCEL_REQUEST_FAILURE:
      case ELIGIBILITY_SUCCESS:
      case HRTV_SUCCESS:
      case RETURNS_500_ERROR:
      case RETURNS_400_ERROR:
      case SSR_CREATE_ERROR:
      case SSR_CREATE_SUCCESS:
        return false;
      default:
        return state;
    }
  };

  const openModal = (state: boolean = false, action: Object) => {
    if (filter !== action.filter) {
      return state;
    }
    switch (action.type) {
      case ACTIONS.CLOSE_MODAL:
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_FAILURE:
      case ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_SUCCESS:
      case ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_FAILURE:
        return false;
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS:
      case ACTIONS.SHOW_EARLY_BODFS_TYT_MODAL:
        return true;
      default:
        return state;
    }
  };

  const errorMessage = (state: any = null, action: Object) => {
    if (filter !== action.filter) {
      return state;
    }
    switch (action.type) {
      case ACTIONS.FETCH_ORDER_DETAILS_FAILURE:
      case ACTIONS.FETCH_ORDER_HISTORY_FAILURE:
      case ACTIONS.FETCH_ERECEIPTS_SUMMARY_FAILURE:
      case ACTIONS.FETCH_SHIPMENT_TRACKING_DETAIL_FAILURE:
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_FAILURE:
      case ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_FAILURE:
      case ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY_FAILURE:
      case ACTIONS.MA_CANCEL_REQUEST_FAILURE:
      case SET_LANDING_ERROR:
      case SET_SELECTED_BOXES_ERROR:
        return action.message;
      case ACTIONS.RESET_ERROR:
      case ACTIONS.FETCH_ORDER_DETAILS:
      case ACTIONS.FETCH_SHIPMENT_TRACKING_DETAIL:
      case ACTIONS.FETCH_BODFS_DELIVERY_DETAIL:
      case ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS:
      case ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY:
      case ACTIONS.MA_CANCEL_REQUEST:
      case CHECKBOX_SELECT:
      case BOXES_SELECT:
      case SSR_RESET:
      case BACK_TO_LANDING:
        return null;
      default:
        return state;
    }
  };

  return combineReducers({
    actionData,
    mappedData,
    isFetching,
    openModal,
    errorMessage,
  });
};

export default orderActions;

export const getActionData = (state: Object, orderId: string = '') => {
  if (orderId) {
    return state ? state.actionData[orderId] : {};
  }
  return state ? state.actionData : {};
};
export const getMappedData = (state: Object, orderId: string = '') => {
  if (orderId) {
    return state ? state.mappedData[orderId] : {};
  }
  return state ? state.mappedData : {};
};
export const getIsFetching = (state: Object, orderId: string = '') => {
  if (orderId) {
    return state ? state.isFetching[orderId] : false;
  }
  return state ? state.isFetching : false;
};
export const getOpenModal = (state: Object, orderId: string = '') => {
  if (orderId) {
    return state ? state.openModal[orderId] : false;
  }
  return state ? state.openModal : false;
};
export const getErrorMessage = (state: Object, orderId: string = '') => {
  if (orderId) {
    return state ? state.errorMessage[orderId] : null;
  }
  return state ? state.errorMessage : null;
};
export const getOrderLineItems = (state: Object, orderId: string = '') => {
  if (!orderId) {
    return [];
  }
  return state.actionData[orderId].lineItems
    ? state.actionData[orderId].lineItems.lineItem
    : [];
};
export const getOrderRefNumber = (state: Object, orderId: string = '') => {
  if (!orderId) {
    return '';
  }
  return state.actionData[orderId].orderRefNumber
    ? state.actionData[orderId].orderRefNumber
    : '';
};

export const getIsInstore = (state: Object, orderId: string = '') => {
  if (!orderId) {
    return '';
  }
  return !isEmpty(state.actionData[orderId])
    ? state.actionData[orderId].isInstore
    : false;
};
